import '.ai{flex-shrink:0;}.ssr-plugin'
import '.ay{height:100%;}.ssr-plugin'
import '.t{padding:1rem;}.ssr-plugin'
import '.d{display:flex;}.ssr-plugin'
import '.e{flex-direction:column;}.ssr-plugin'
import '.r{align-items:stretch;}.ssr-plugin'
import '.y{gap:0.5rem;}.ssr-plugin'
import '.a6{min-width:0px;}.ssr-plugin'
import '.az{flex:1;}.ssr-plugin'
import { Style, styled } from 'inlines'
import { Text } from '@app/components/core/typography/Text'
import {
  Children,
  PropsWithChildren,
  ReactNode,
  isValidElement,
  useMemo,
} from 'react'
import { Image } from '@app/components/core/image/Image'
import { LinkAnchor } from '@app/components/core/link/LinkAnchor'

export interface CardProps extends PropsWithChildren {
  image?: string | ReactNode
  title?: string
  subtitle?: string
  orientation?: 'vertical' | 'horizontal'
  padded?: boolean
  contentStyle?: Style
  border?: boolean
  style?: Style
  imageStyle?: Style
  imageObjectFit?: 'cover' | 'contain'
  imageObjectPosition?: string
  to?: string
  onClick?: () => void
  actionsAlign?: 'start' | 'end'
  titleStyle?: Style
  'data-testid'?: string
}

const CardActions = ({ children }: { children?: ReactNode }) => {
  return <>{children}</>
}

const CardMeta = ({ children }: { children?: ReactNode }) => {
  return <>{children}</>
}

const CardComponent = ({
  image,
  title,
  subtitle,
  orientation = 'vertical',
  padded,
  border = true,
  style,
  imageStyle,
  imageObjectFit = 'cover',
  imageObjectPosition = 'center',
  contentStyle,
  to,
  onClick,
  actionsAlign = 'end',
  titleStyle,
  children,
  'data-testid': testId = 'card',
}: CardProps) => {
  const { actions, meta, content } = useMemo(() => {
    const childrenArray = Children.toArray(children)
    let actions
    let meta
    const content: ReactNode[] = []

    for (const child of childrenArray) {
      if (isValidElement(child)) {
        if (child.type === CardActions) {
          if (!actions) {
            actions = child
          }
          continue
        }
        if (child.type === CardMeta) {
          if (!meta) {
            meta = child
          }
          continue
        }
      }
      content.push(child)
    }

    return { actions, meta, content }
  }, [children])

  const isInteractive = Boolean(to || onClick)
  const rootStyle: Style = {
    padding: 0,
    position: 'relative',
    backgroundColor: 'var(--card-bg)',
    borderRadius: border ? '20px' : '0',
    border: border ? 'var(--card-border)' : 'none',
    overflow: 'hidden',
    cursor: isInteractive ? 'pointer' : 'default',
    width: '100%',
    display: 'flex',
    flexDirection: orientation === 'horizontal' ? 'row' : 'column',
    gap: orientation === 'horizontal' ? '1rem' : undefined,
    alignItems: orientation === 'horizontal' ? 'stretch' : undefined,
    transition: 'transform 0.2s ease, opacity 0.2s ease',
    textAlign: 'left',
    ...(onClick && !to
      ? {
          appearance: 'none',
          fontFamily: 'inherit',
          fontSize: 'inherit',
        }
      : {}),
    '&:hover': isInteractive
      ? {
          transform: 'translateY(-2px)',
          opacity: 0.9,
        }
      : {},
    ...style,
  }

  const contentNode = (
    <>
      {/* Image Section */}
      {image && (
        <styled.div className="ai"
          style={{
            padding: padded
              ? orientation === 'horizontal'
                ? '1rem 0 1rem 1rem'
                : '1rem 1rem 0 1rem'
              : 0,
            width: orientation === 'horizontal' ? '140px' : '100%',
            minWidth: orientation === 'horizontal' ? '140px' : undefined,
            height: orientation === 'horizontal' ? undefined : '192px',
            ...imageStyle,
          }}
        >
          {isValidElement(image) ? (
            image
          ) : (
            <Image
              src={image as string}
              alt={title || ''}
              style={{
                borderRadius: padded ? '10px' : 0,
                height: '100%',
                width: '100%',
                objectFit: imageObjectFit,
                objectPosition: imageObjectPosition,
              }}
            />
          )}
        </styled.div>
      )}

      {/* Content Section */}
      <styled.div className="ay t d e r y a6"
        style={{
          flex: orientation === 'horizontal' ? 1 : undefined,
          position: orientation === 'horizontal' ? undefined : 'relative',
          ...contentStyle,
        }}
      >
        {/* Category Tag */}
        {meta && meta.props.children}

        {/* Title */}
        {title && (
          <div>
            <Text.Large
              style={{
                color: 'var(--brand-secondary)',
                margin: 0,
                fontWeight: '500',
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                ...titleStyle,
              }}
            >
              {title}
            </Text.Large>
            {subtitle && (
              <Text.Small
                style={{
                  fontSize: '0.875rem',
                  color: 'var(--palette-blue-500)',
                  fontWeight: '500',
                  width: '100%',
                  lineHeight: '100%',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                }}
              >
                {subtitle}
              </Text.Small>
            )}
          </div>
        )}

        {content}

        {/* Spacer to push actions down */}
        {actions && <div className="az" />}

        {/* Actions */}
        {actions && (
          <div className="d y"
            style={{
              alignSelf: actionsAlign === 'start' ? 'flex-start' : 'flex-end',}}
          >
            {actions.props.children}
          </div>
        )}
      </styled.div>
    </>
  )

  if (to) {
    return (
      <LinkAnchor
        data-testid={testId}
        onClick={onClick ? () => onClick() : undefined}
        style={rootStyle}
        to={to}
      >
        {contentNode}
      </LinkAnchor>
    )
  }

  if (onClick) {
    return (
      <styled.button data-testid={testId} onClick={onClick} style={rootStyle}>
        {contentNode}
      </styled.button>
    )
  }

  return (
    <styled.div data-testid={testId} style={rootStyle}>
      {contentNode}
    </styled.div>
  )
}

export const Card = Object.assign(CardComponent, {
  Actions: CardActions,
  Meta: CardMeta,
})
